import React, { useCallback } from 'react';

import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onKnowledgeCardLinkClick } from '../../../knowledge-cards/analytics';
import { useSAINUsageCriteriaMet } from './hooks/use-sain-usage';
import { type PeopleTeamsPayload, resolvePeopleTeamsAnalytics } from './people-teams-analytics-map';
import { useAnalytics } from './use-analytics';

type PeopleTeamsAnalyticsListenerProps = {
	children: React.ReactNode;
};

/**
 * Wraps `children` with an `AnalyticsListener` for the `peopleTeams` channel
 * and translates native click events fired by people-and-teams components
 * (e.g. `UserProfilePreview`, `UserInlinePreview`, `HierarchyChart`) into SAIN
 * `aiAnswerDialog clicked` track events plus matching SAIN usage (MAU) events.
 *
 * This abstraction is shared between the `PersonKnowledgeCard` and the
 * `SmartAnswerCardConsolidated` so that people-teams event instrumentation
 * stays consistent without duplicating the mapping or listener wiring. The pure
 * mapping lives in `people-teams-analytics-map.ts`.
 */
export const PeopleTeamsAnalyticsListener = ({
	children,
}: PeopleTeamsAnalyticsListenerProps): React.JSX.Element => {
	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const handlePeopleTeamsEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const resolved = resolvePeopleTeamsAnalytics(event.payload as PeopleTeamsPayload);
			if (!resolved) {
				return;
			}

			// Fire the `aiAnswerDialog clicked` track event (existing location-based pattern).
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location: resolved.location,
				}),
			);

			// Fire the matching SAIN usage (MAU) event for parity with the existing person card
			// instrumentation in person-card-body.tsx.
			if (resolved.sainAction) {
				fireSAINUsageEvent({ metCriteria: resolved.sainAction, commonAttributes });
			}
		},
		[fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes],
	);

	return (
		<AnalyticsListener channel="peopleTeams" onEvent={handlePeopleTeamsEvent}>
			{children}
		</AnalyticsListener>
	);
};
